import React, { createContext, useContext, useState, ReactNode } from 'react';
import Toast, { ToastType } from '../components/common/Toast';

interface ToastContextType {
  showToast: (type: ToastType, message: string, duration?: number) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toast, setToast] = useState<{
    visible: boolean;
    type: ToastType;
    message: string;
    duration: number;
  } | null>(null);

  const showToast = (type: ToastType, message: string, duration = 5000) => {
    setToast({ visible: true, type, message, duration });
  };

  const hideToast = () => {
    setToast(prev => prev ? { ...prev, visible: false } : null);
    
    // Completely remove the toast after animation
    setTimeout(() => {
      setToast(null);
    }, 300);
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          duration={toast.duration}
          visible={toast.visible}
          onClose={hideToast}
        />
      )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};